import React, { useState } from 'react';
import { Sparkles, Lock, Check } from '../../icons';
import Sheet from '../ui/Sheet';
import Button from '../ui/Button';
import Toggle from '../ui/Toggle';

const PROVIDERS = [
  { id: 'gemini', label: 'Google Gemini', models: ['gemini-3.6-flash', 'gemini-3-flash-preview', 'gemini-2.5-flash', 'gemini-2.5-pro'] },
  { id: 'claude', label: 'Anthropic Claude', models: ['claude-opus-4-8', 'claude-sonnet-4-6', 'claude-haiku-4-5'] },
  { id: 'openai', label: 'OpenAI', models: ['gpt-5.5', 'gpt-5.4', 'gpt-5.4-mini'] },
  { id: 'perplexity', label: 'Perplexity', models: ['sonar-pro', 'sonar', 'sonar-reasoning'] },
];

/**
 * Admin per-user AI controls. Policy only — admins never see or set the user's
 * API key (key material lives in a deny-all private subdoc).
 */
const UserAIModal = ({ user, onClose, onSave }) => {
  const ai = user.ai || {};
  const [form, setForm] = useState({
    provider: ai.provider || '',
    model: ai.model || '',
    byokDisabled: ai.byokDisabled === true,
    useHouseKey: ai.useHouseKey === true,
    lockedByAdmin: ai.lockedByAdmin === true,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const change = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setMessage(null);
  };

  const models = PROVIDERS.find((p) => p.id === form.provider)?.models || [];

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await onSave(user.id, form);
      setMessage({ type: 'success', text: 'Saved.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const Row = ({ label, hint, value, onToggle }) => (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-sm text-primary">{label}</div>
        {hint && <p className="text-xs text-muted">{hint}</p>}
      </div>
      <Toggle checked={value} onChange={onToggle} label={label} size="sm" className="mt-0.5" />
    </div>
  );

  return (
    <Sheet
      isOpen
      onClose={onClose}
      mode="sheet"
      size="sm"
      title="AI settings"
      subtitle={user.displayName}
      headerLeft={
        <span className="mt-1.5 w-10 h-10 rounded-xl bg-accent-soft text-accent flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5" />
        </span>
      }
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button variant="primary" onClick={save} disabled={saving} loading={saving}>
            {!saving && <Check className="w-4 h-4" />} Save
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="text-xs text-secondary bg-page border border-line rounded-xl p-2.5 flex items-start gap-2">
          <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>Key status: {ai.hasKey ? `set ••••${ai.last4 || ''} (${ai.keyProvider || '?'})` : 'no key'} — admins cannot view or set keys.</span>
        </div>

        <div>
          <label className="label" htmlFor="user-ai-provider">Provider override</label>
          <select
            id="user-ai-provider"
            value={form.provider}
            onChange={(e) => {
              const p = e.target.value;
              change({ provider: p, model: PROVIDERS.find((x) => x.id === p)?.models?.[0] || '' });
            }}
            className="input"
          >
            <option value="">(use org default)</option>
            {PROVIDERS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </div>

        {form.provider && (
          <div>
            <label className="label" htmlFor="user-ai-model">Model override</label>
            <select id="user-ai-model" value={form.model} onChange={(e) => change({ model: e.target.value })} className="input">
              {models.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        )}

        <div className="space-y-3 pt-3 border-t border-line">
          <Row label="Disable BYOK" hint="User cannot add their own key." value={form.byokDisabled} onToggle={() => change({ byokDisabled: !form.byokDisabled })} />
          <Row label="Use house key" hint="Org-provided key (hidden from user). Requires HOUSE_<PROVIDER>_KEY secret." value={form.useHouseKey} onToggle={() => change({ useHouseKey: !form.useHouseKey })} />
          <Row label="Lock configuration" hint="User cannot change provider/model/key." value={form.lockedByAdmin} onToggle={() => change({ lockedByAdmin: !form.lockedByAdmin })} />
        </div>

        {message && (
          <div className={`p-3 rounded-xl text-sm border ${message.type === 'success' ? 'bg-accent-soft text-accent border-accent/30' : 'bg-danger-soft text-danger border-danger/30'}`}>
            {message.text}
          </div>
        )}
      </div>
    </Sheet>
  );
};

export default UserAIModal;
